// BOX shape mode — drag a 3D box anchored at the press-time cell.
//
// DRAW: the laser sees through cells the gesture has placed
// (voxelRaycastSkip = placedKeys), so the pick keeps reporting the
// ORIGINAL surface; the far corner is the empty cell adjacent to the
// picked face (or a surface cell). Sweeping along the original face
// grows a flat slab; dragging up an adjacent wall extrudes the box
// vertically — MagicaVoxel-style.
//
// ERASE: the laser is BLOCKED by cells the gesture has erased
// (voxelRaycastInclude = placedKeys treats them as still-occupied for
// pick purposes), so the pick stops at the deleted face and the
// cursor floats on it. The far corner is just the picked voxel. As
// the user moves the laser to a new voxel face, that voxel is the
// new far corner and gets erased. Wall-following emerges naturally
// when the user aims at a side face whose laser path doesn't pass
// through an already-erased cell.
//
// On press:
//   • DRAW start cell is one cell outward from the picked face (the
//     empty cell about to be filled).
//   • ERASE start cell is the picked face's own voxel.
//   • Surface picks are only valid for DRAW.
//
// Release commits whatever the final box produced.

import { controllerStates } from "../../input/controllers";
import {
  type ChunkStorage,
  type PaletteIndex,
  type VoxelGrid,
} from "../../model/voxel-grid";
import { disableCoalescing, recordSnapshot } from "../../model/history";
import { mirrorBoxes, mirrorCells } from "../mirror";
import { type CellBounds, type PickTarget, cellInBounds } from "../pick";
import { interaction, setInteraction } from "../state";
import type { Interaction } from "../types";
import {
  cellKey,
  isDrawKind,
  isRecolorKind,
  planeFromPick,
  targetCellFromPick,
} from "./shape-fill";

type BoxState = Extract<
  Interaction,
  { kind: "box_draw" } | { kind: "box_erase" } | { kind: "box_recolor" }
>;

export function startBox(
  pick: PickTarget,
  controllerIndex: number,
  action: "draw" | "erase" | "recolor",
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  const plane = planeFromPick(pick, action);
  if (!plane) return;

  // draw/erase/recolor share BoxBase; only the kind tag (and thus the
  // reconcile behaviour) differs.
  const kind =
    action === "draw"
      ? "box_draw"
      : action === "erase"
        ? "box_erase"
        : "box_recolor";
  const state: BoxState = {
    kind,
    controllerIndex,
    planeAxis: plane.planeAxis,
    planeCoord: plane.planeCoord,
    planeNormalSign: plane.planeNormalSign,
    startCell: { ...plane.startCell },
    currentCell: { ...plane.startCell },
    applied: new Map(),
    placedKeys: new Set(),
  };
  setInteraction(state);

  // Apply the initial 1-cell box immediately. Press always commits the
  // starting cell; drag grows from there.
  syncBoxRect(state, grid, bounds, paletteIndex);
}

export function handleBoxDrag(
  lastPicks: ReadonlyArray<PickTarget | null>,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  if (
    interaction.kind !== "box_draw" &&
    interaction.kind !== "box_erase" &&
    interaction.kind !== "box_recolor"
  ) {
    return;
  }
  const state = interaction;
  if (!controllerStates[state.controllerIndex].selecting) return;

  // Pick-driven: the far corner is the hovered floor/wall/voxel cell, so the
  // box grows in 3D (drag up an adjacent wall to extrude). When the cursor is
  // off every surface (pick null) the box holds; on desktop, updateDesktopBox
  // fills to the volume edge as a fallback for that case.
  const end = targetCellFromPick(lastPicks[state.controllerIndex], isDrawKind(state.kind));
  if (!end) return;
  setBoxFarCorner(end.ix, end.iy, end.iz, grid, bounds, paletteIndex);
}

// Set the box's opposite corner directly (clamped happens in syncBoxRect's
// bounds check) and re-reconcile the preview. Shared by the VR pick path and
// the desktop plane-projection driver.
export function setBoxFarCorner(
  ix: number,
  iy: number,
  iz: number,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  if (
    interaction.kind !== "box_draw" &&
    interaction.kind !== "box_erase" &&
    interaction.kind !== "box_recolor"
  ) {
    return;
  }
  const state = interaction;
  if (
    ix === state.currentCell.ix &&
    iy === state.currentCell.iy &&
    iz === state.currentCell.iz
  ) {
    return; // no change
  }
  state.currentCell = { ix, iy, iz };
  syncBoxRect(state, grid, bounds, paletteIndex);
}

// Axis-aligned cell box (inclusive corners), in cell coords.
interface AABB {
  x0: number; x1: number; y0: number; y1: number; z0: number; z1: number;
}

function boxFrom(a: BoxState["startCell"], b: BoxState["currentCell"]): AABB {
  return {
    x0: Math.min(a.ix, b.ix), x1: Math.max(a.ix, b.ix),
    y0: Math.min(a.iy, b.iy), y1: Math.max(a.iy, b.iy),
    z0: Math.min(a.iz, b.iz), z1: Math.max(a.iz, b.iz),
  };
}

function inBox(b: AABB, ix: number, iy: number, iz: number): boolean {
  return (
    ix >= b.x0 && ix <= b.x1 &&
    iy >= b.y0 && iy <= b.y1 &&
    iz >= b.z0 && iz <= b.z1
  );
}

// A cell is still covered by the box (under mirror) iff any of its mirror images
// lands inside the box — the per-cell replacement for reconcileFill's global
// `desired` set, so a mirrored cell isn't peeled while its twin remains inside.
function coveredBy(box: AABB, ix: number, iy: number, iz: number): boolean {
  for (const m of mirrorCells(ix, iy, iz)) {
    if (inBox(box, m.ix, m.iy, m.iz)) return true;
  }
  return false;
}

// Visit the primary cells in A that are NOT in B (B null ⇒ all of A). The three
// slabs partition A∖B with no overlap, so a one-cell growth walks only the new
// layer instead of the whole volume — the crux of the incremental reconcile.
function forEachDiff(
  A: AABB,
  B: AABB | null,
  cb: (ix: number, iy: number, iz: number) => void,
): void {
  if (!B) {
    for (let x = A.x0; x <= A.x1; x++)
      for (let y = A.y0; y <= A.y1; y++)
        for (let z = A.z0; z <= A.z1; z++) cb(x, y, z);
    return;
  }
  // Slab 1: x outside B (full A in y, z).
  for (let x = A.x0; x <= A.x1; x++) {
    if (x >= B.x0 && x <= B.x1) continue;
    for (let y = A.y0; y <= A.y1; y++)
      for (let z = A.z0; z <= A.z1; z++) cb(x, y, z);
  }
  // Slab 2: x within A∩B, y outside B (full A in z).
  const ix0 = Math.max(A.x0, B.x0), ix1 = Math.min(A.x1, B.x1);
  for (let x = ix0; x <= ix1; x++) {
    for (let y = A.y0; y <= A.y1; y++) {
      if (y >= B.y0 && y <= B.y1) continue;
      for (let z = A.z0; z <= A.z1; z++) cb(x, y, z);
    }
  }
  // Slab 3: x, y within A∩B, z outside B.
  const iy0 = Math.max(A.y0, B.y0), iy1 = Math.min(A.y1, B.y1);
  for (let x = ix0; x <= ix1; x++)
    for (let y = iy0; y <= iy1; y++)
      for (let z = A.z0; z <= A.z1; z++) {
        if (z >= B.z0 && z <= B.z1) continue;
        cb(x, y, z);
      }
}

// Like forEachDiff but yields the SLAB regions of A∖B (inclusive AABBs) instead
// of individual cells — so a live edit can bulk-process the swept shell in a
// handful of clearBox calls rather than per cell. The slabs partition A∖B with
// no overlap.
function forEachDiffSlab(
  A: AABB,
  B: AABB | null,
  cb: (x0: number, x1: number, y0: number, y1: number, z0: number, z1: number) => void,
): void {
  if (!B) {
    cb(A.x0, A.x1, A.y0, A.y1, A.z0, A.z1);
    return;
  }
  // X slabs: x in A but outside B (full A in y, z).
  if (A.x0 < B.x0) cb(A.x0, Math.min(A.x1, B.x0 - 1), A.y0, A.y1, A.z0, A.z1);
  if (A.x1 > B.x1) cb(Math.max(A.x0, B.x1 + 1), A.x1, A.y0, A.y1, A.z0, A.z1);
  const ix0 = Math.max(A.x0, B.x0), ix1 = Math.min(A.x1, B.x1);
  if (ix0 > ix1) return;
  // Y slabs: x in A∩B, y outside B (full A in z).
  if (A.y0 < B.y0) cb(ix0, ix1, A.y0, Math.min(A.y1, B.y0 - 1), A.z0, A.z1);
  if (A.y1 > B.y1) cb(ix0, ix1, Math.max(A.y0, B.y1 + 1), A.y1, A.z0, A.z1);
  const iy0 = Math.max(A.y0, B.y0), iy1 = Math.min(A.y1, B.y1);
  if (iy0 > iy1) return;
  // Z slabs: x, y in A∩B, z outside B.
  if (A.z0 < B.z0) cb(ix0, ix1, iy0, iy1, A.z0, Math.min(A.z1, B.z0 - 1));
  if (A.z1 > B.z1) cb(ix0, ix1, iy0, iy1, Math.max(A.z0, B.z1 + 1), A.z1);
}

// State subset reconcileBoxIncremental needs — BoxState satisfies it; declaring
// it structurally keeps the reconcile unit-testable without a full Interaction.
export interface BoxReconcileState {
  kind: BoxState["kind"];
  startCell: BoxState["startCell"];
  currentCell: BoxState["currentCell"];
  applied: BoxState["applied"];
  placedKeys: BoxState["placedKeys"];
  appliedBox?: AABB | null;
}

// Incremental reconcile: bring the grid from the box reflected in `appliedBox`
// to the box defined by start/current, touching only the cells that left or
// entered (each expanded through mirrorCells). Equivalent to reconcileFill over
// the full volume, but O(changed shell) per frame instead of O(volume) — proven
// by box.test.ts. Mirror reflections and the draw/erase/recolor branches mirror
// reconcileFill exactly.
export function reconcileBoxIncremental(
  state: BoxReconcileState,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  const draw = isDrawKind(state.kind);
  const recolor = isRecolorKind(state.kind);
  const newBox = boxFrom(state.startCell, state.currentCell);
  const oldBox = state.appliedBox ?? null;

  // Restore primaries that left the box; peel each mirror image no longer
  // covered (its twin may still sit inside, in which case it stays).
  if (oldBox) {
    forEachDiff(oldBox, newBox, (ix, iy, iz) => {
      for (const m of mirrorCells(ix, iy, iz)) {
        const key = cellKey(m.ix, m.iy, m.iz);
        const entry = state.applied.get(key);
        if (!entry) continue;
        if (coveredBy(newBox, m.ix, m.iy, m.iz)) continue;
        if (draw) grid.delete(entry.ix, entry.iy, entry.iz);
        else grid.set(entry.ix, entry.iy, entry.iz, entry.originalPalette);
        state.applied.delete(key);
        state.placedKeys.delete(key);
      }
    });
  }

  // Apply primaries newly inside the box (and their mirror images).
  forEachDiff(newBox, oldBox, (ix, iy, iz) => {
    for (const m of mirrorCells(ix, iy, iz)) {
      const key = cellKey(m.ix, m.iy, m.iz);
      if (state.applied.has(key)) continue;
      if (!cellInBounds(m.ix, m.iy, m.iz, bounds)) continue;
      if (draw) {
        if (grid.has(m.ix, m.iy, m.iz)) continue; // leave others' cells alone
        grid.set(m.ix, m.iy, m.iz, paletteIndex);
        state.applied.set(key, { ix: m.ix, iy: m.iy, iz: m.iz, originalPalette: 0 });
        state.placedKeys.add(key);
      } else if (recolor) {
        const existing = grid.get(m.ix, m.iy, m.iz);
        if (existing === 0) continue; // recolor only existing voxels
        grid.set(m.ix, m.iy, m.iz, paletteIndex);
        state.applied.set(key, { ix: m.ix, iy: m.iy, iz: m.iz, originalPalette: existing });
        state.placedKeys.add(key);
      } else {
        const existing = grid.get(m.ix, m.iy, m.iz);
        if (existing === 0) continue; // nothing to delete
        grid.delete(m.ix, m.iy, m.iz);
        state.applied.set(key, { ix: m.ix, iy: m.iy, iz: m.iz, originalPalette: existing });
        state.placedKeys.add(key);
      }
    }
  });

  state.appliedBox = newBox;
}

// Reconcile the grid to the current box AABB.
//
// main injects the whole-grid snapshot capture (it owns the full ModelSnapshot).
let captureSnapshot: (() => unknown) | null = null;
export function setBoxSnapshotCapture(fn: (() => unknown) | null): void {
  captureSnapshot = fn;
}
// Snapshots taken at a box_erase gesture's first frame: `eraseSnapBefore` (the
// ModelSnapshot) records the one undo step at commit; `eraseRawBefore` (raw
// chunk bytes) lets a back-drag RESTORE cells to their pre-erase state.
let eraseSnapBefore: unknown = null;
let eraseRawBefore: Map<number, ChunkStorage> | null = null;
// Same snapshots for a LIVE box_draw (raymarch renderer): the fill is written
// each frame instead of deferred to a preview shell, so the cube + the growing
// box are one renderer (no shading seam / leftover edge from the mesh shell).
let drawSnapBefore: unknown = null;
let drawRawBefore: Map<number, ChunkStorage> | null = null;
let liveDrawEnabled = false;
export function setBoxLiveDraw(on: boolean): void {
  liveDrawEnabled = on;
}

// Apply f to every mirror reflection of a slab, clamped to the work volume, as
// half-open box bounds.
function forEachMirroredSlab(
  slab: { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number },
  bounds: CellBounds,
  f: (x0: number, x1: number, y0: number, y1: number, z0: number, z1: number) => void,
): void {
  for (const b of mirrorBoxes(slab)) {
    const x0 = Math.max(b.x0, bounds.minIx), x1 = Math.min(b.x1, bounds.maxIx - 1);
    const y0 = Math.max(b.y0, bounds.minIy), y1 = Math.min(b.y1, bounds.maxIy - 1);
    const z0 = Math.max(b.z0, bounds.minIz), z1 = Math.min(b.z1, bounds.maxIz - 1);
    if (x1 < x0 || y1 < y0 || z1 < z0) continue;
    f(x0, x1 + 1, y0, y1 + 1, z0, z1 + 1);
  }
}

// Live box-erase: each frame, bulk-clear the cells the box newly covers and
// restore the cells it no longer covers (drag back to un-erase — the opposite of
// box-draw). Direct chunk-array writes, no per-cell delete/history, so it stays
// smooth; the worker re-meshes off-thread. Undo is one whole-grid snapshot.
function liveEraseBox(state: BoxState, grid: VoxelGrid, bounds: CellBounds): void {
  const newBox = boxFrom(state.startCell, state.currentCell);
  const oldBox = state.appliedBox ?? null;
  if (oldBox === null) {
    eraseSnapBefore = captureSnapshot?.() ?? null;
    eraseRawBefore = grid.snapshotChunks();
  }
  // Newly inside the box → carve.
  forEachDiffSlab(newBox, oldBox, (sx0, sx1, sy0, sy1, sz0, sz1) => {
    forEachMirroredSlab({ x0: sx0, x1: sx1, y0: sy0, y1: sy1, z0: sz0, z1: sz1 }, bounds, (x0, x1, y0, y1, z0, z1) =>
      grid.clearBox(x0, x1, y0, y1, z0, z1),
    );
  });
  // No longer inside the box (back-drag) → restore to pre-erase state.
  if (oldBox !== null && eraseRawBefore) {
    const snap = eraseRawBefore;
    forEachDiffSlab(oldBox, newBox, (sx0, sx1, sy0, sy1, sz0, sz1) => {
      forEachMirroredSlab({ x0: sx0, x1: sx1, y0: sy0, y1: sy1, z0: sz0, z1: sz1 }, bounds, (x0, x1, y0, y1, z0, z1) =>
        grid.restoreBoxFrom(snap, x0, x1, y0, y1, z0, z1),
      );
    });
  }
  state.appliedBox = newBox;
}

// Live box-draw: the mirror of liveEraseBox — each frame, bulk-FILL the cells the
// box newly covers and restore the cells it no longer covers (drag back to
// un-draw). skipFilled leaves others' voxels intact. Only used when the
// raymarcher is the renderer (an edit is a cheap texture upload).
function liveDrawBox(
  state: BoxState,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  const newBox = boxFrom(state.startCell, state.currentCell);
  const oldBox = state.appliedBox ?? null;
  if (oldBox === null) {
    drawSnapBefore = captureSnapshot?.() ?? null;
    drawRawBefore = grid.snapshotChunks();
  }
  // Newly inside the box → fill.
  forEachDiffSlab(newBox, oldBox, (sx0, sx1, sy0, sy1, sz0, sz1) => {
    forEachMirroredSlab({ x0: sx0, x1: sx1, y0: sy0, y1: sy1, z0: sz0, z1: sz1 }, bounds, (x0, x1, y0, y1, z0, z1) =>
      grid.fillBox(x0, x1, y0, y1, z0, z1, paletteIndex, true),
    );
  });
  // No longer inside (back-drag) → restore to pre-draw state.
  if (oldBox !== null && drawRawBefore) {
    const snap = drawRawBefore;
    forEachDiffSlab(oldBox, newBox, (sx0, sx1, sy0, sy1, sz0, sz1) => {
      forEachMirroredSlab({ x0: sx0, x1: sx1, y0: sy0, y1: sy1, z0: sz0, z1: sz1 }, bounds, (x0, x1, y0, y1, z0, z1) =>
        grid.restoreBoxFrom(snap, x0, x1, y0, y1, z0, z1),
      );
    });
  }
  state.appliedBox = newBox;
}

// box_draw is previewed as a surface shell and committed in bulk on release —
// unless the raymarcher is active, when it writes live (liveDrawBox). box_erase
// carves live (liveEraseBox) but in BULK per frame. box_recolor stays live
// per-cell: it only recolours existing voxels (cheap, no exposed interior).
function syncBoxRect(
  state: BoxState,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  if (state.kind === "box_draw") {
    if (liveDrawEnabled && captureSnapshot) liveDrawBox(state, grid, bounds, paletteIndex);
    return; // else: preview + bulk commit
  }
  if (state.kind === "box_erase") {
    if (captureSnapshot) liveEraseBox(state, grid, bounds);
    else reconcileBoxIncremental(state, grid, bounds, 0); // fallback: per-cell live
    return;
  }
  reconcileBoxIncremental(state, grid, bounds, paletteIndex);
}

export function commitBoxDraw(
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
): void {
  if (interaction.kind !== "box_draw") return;
  const state = interaction; // narrowed; const so the closure keeps the type
  if (liveDrawEnabled && captureSnapshot && drawSnapBefore !== null) {
    // Live draw already filled each frame (liveDrawBox); record the one undo step.
    recordSnapshot(drawSnapBefore, captureSnapshot());
    drawSnapBefore = null;
    drawRawBefore = null;
    return;
  }
  if (captureSnapshot) {
    // Bulk masked fill + ONE whole-grid snapshot for undo (no per-cell writes or
    // history). The box AABB and its mirror reflections are filled directly into
    // the chunk arrays, empties only (draw-skip), clamped to the work volume —
    // equivalent to reconcileBoxIncremental's box_draw path but ~15x cheaper.
    const before = captureSnapshot();
    const box = boxFrom(state.startCell, state.currentCell); // inclusive AABB
    for (const b of mirrorBoxes(box)) {
      const x0 = Math.max(b.x0, bounds.minIx), x1 = Math.min(b.x1, bounds.maxIx - 1);
      const y0 = Math.max(b.y0, bounds.minIy), y1 = Math.min(b.y1, bounds.maxIy - 1);
      const z0 = Math.max(b.z0, bounds.minIz), z1 = Math.min(b.z1, bounds.maxIz - 1);
      if (x1 < x0 || y1 < y0 || z1 < z0) continue;
      // half-open bounds for fillBox; skipFilled leaves others' voxels intact
      grid.fillBox(x0, x1 + 1, y0, y1 + 1, z0, z1 + 1, paletteIndex, true);
    }
    recordSnapshot(before, captureSnapshot());
  } else {
    // Fallback (capture not wired): per-cell history; no coalescing needed since
    // the one-shot fill touches each cell once.
    disableCoalescing();
    reconcileBoxIncremental(state, grid, bounds, paletteIndex);
  }
}

// box_erase release: the carve already happened live (liveEraseBox). Record the
// gesture's one undo step — the before-snapshot taken on its first frame vs the
// current state. (The per-cell fallback path recorded history live, so there's
// nothing to do there.)
export function commitBoxErase(_grid: VoxelGrid, _bounds: CellBounds): void {
  if (interaction.kind !== "box_erase") return;
  if (captureSnapshot && eraseSnapBefore !== null) {
    recordSnapshot(eraseSnapBefore, captureSnapshot());
  }
  eraseSnapBefore = null;
  eraseRawBefore = null; // release the per-stroke restore buffer
}
